import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { DocumentEditor as OnlyOfficeEditor } from '@onlyoffice/document-editor-react';
import { ArrowLeft, Download, History, Loader2, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { documentsAPI } from '@/lib/api';
import { editorAPI } from '@/lib/editorAPI';
import { safeFormat } from '@/lib/utils';

// Full-screen editor for one document. Saving happens on the editor server's side: it calls the backend
// back with the new file when people close it (and every few minutes while it's open).
export default function DocumentEditorPage() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [versions, setVersions] = useState([]);

  useEffect(() => {
    let live = true;
    editorAPI.config(id, params.get('mode') || 'edit')
      .then((r) => { if (live) setData(r.data); })
      .catch((e) => { if (live) setError(e.response?.data?.detail || 'This document could not be opened.'); });
    return () => { live = false; };
  }, [id, params]);

  const loadVersions = () => editorAPI.versions(id).then((r) => setVersions(r.data)).catch(() => setVersions([]));
  const back = () => (window.history.length > 1 ? navigate(-1) : navigate('/documents'));
  const filename = data?.config?.document?.title;

  return (
    <div className="h-screen w-full flex flex-col bg-[#F3EFE7]">
      <header className="flex items-center gap-3 px-3 py-2 border-b border-[#DAD3C6] bg-white">
        <Button variant="ghost" size="sm" className="gap-1.5" onClick={back}>
          <ArrowLeft className="w-4 h-4" /> Back
        </Button>
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-[#1E2A33] truncate">{data?.title || 'Document'}</p>
          {data && (
            <p className="text-xs text-[#5C6670] flex items-center gap-1">
              {data.editable ? 'Changes save automatically' : <><Lock className="w-3 h-3" /> {data.locked ? 'Out for signature or signed: read only' : 'Read only'}</>}
            </p>
          )}
        </div>
        {data && (
          <>
            <DropdownMenu onOpenChange={(o) => o && loadVersions()}>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="gap-1.5"><History className="w-4 h-4" /> Versions</Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64">
                <DropdownMenuLabel>Earlier versions</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {versions.length === 0 && <div className="px-2 py-1.5 text-sm text-[#5C6670]">None yet. A version is kept each time an editing session saves.</div>}
                {versions.map((v) => (
                  <DropdownMenuItem key={v.n} onClick={() => editorAPI.downloadVersion(id, v.n, filename && filename.replace(/(\.[^.]+)$/, ` (v${v.n})$1`))}>
                    <Download className="w-3.5 h-3.5 mr-2" /> Version {v.n} · {safeFormat(v.saved_at, 'MMM d, h:mm a')}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            <Button variant="outline" size="sm" className="gap-1.5" onClick={() => documentsAPI.download(id, filename).catch(() => toast.error('Download failed'))}>
              <Download className="w-4 h-4" /> Download
            </Button>
          </>
        )}
      </header>
      <main className="flex-1 min-h-0">
        {error && (
          <div className="h-full flex items-center justify-center p-6">
            <div className="max-w-md text-center">
              <p className="font-semibold text-[#1E2A33]">Can't open the editor</p>
              <p className="text-sm text-[#5C6670] mt-2">{error}</p>
              <Button className="mt-4" variant="outline" onClick={back}>Go back</Button>
            </div>
          </div>
        )}
        {!error && !data && (
          <div className="h-full flex items-center justify-center text-[#5C6670] gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Opening…</div>
        )}
        {data && (
          <OnlyOfficeEditor
            id={`sh-editor-${id}`}
            documentServerUrl={`${data.server_url}/`}
            config={{ ...data.config, width: '100%', height: '100%' }}
            onLoadComponentError={(code, msg) => setError(msg || `The editor server didn't respond (${code}).`)}
          />
        )}
      </main>
    </div>
  );
}
